import Flag from 'LuxKit/Flag'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import config from 'constants/config'
import { REGION_DROPDOWN_START_ORDER } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import setRegionCookie from 'cookies/setRegionCookie'
import { partitionBy, sortBy } from 'lib/array/arrayUtils'
import { removeRegionCode } from 'lib/string/regionUtils'
import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router'
import styled from 'styled-components'

const priorityRegionCodes = new Set(REGION_DROPDOWN_START_ORDER)

const RegionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(220)}, 1fr));
  gap: ${rem(8)};
`

const RegionSection = styled.div`
  & + & {
    margin-top: ${rem(20)};
  }
`

const RegionItem = styled(ClickableListItem)`
  height: 100%;
`

const SectionTitle = styled(BodyText)`
  margin-bottom: ${rem(12)};
`

interface Props {
  onRegionSelect?: (e: React.MouseEvent<HTMLButtonElement>, changed: boolean) => void
}

function NavBarRegionItems(props: Props) {
  const { onRegionSelect } = props
  const { pathname, search } = useLocation()
  const { currentRegionCode } = useContext(GeoContext)
  const [priorityRegions, otherRegions] = useMemo(() => {
    return partitionBy(config.regions, region => priorityRegionCodes.has(region.code))
  }, [])

  // We want our biggest markets at the top of the list, in a specific order
  const sortedRegions = useMemo(() => {
    return sortBy(priorityRegions, (region) => {
      return REGION_DROPDOWN_START_ORDER.indexOf(region.code)
    }, 'asc')
  }, [priorityRegions])

  const regionlessUrl = useMemo(() => {
    return `${removeRegionCode(pathname, currentRegionCode)}${search ?? ''}`
  }, [currentRegionCode, pathname, search])

  const onRegionClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const code = e.currentTarget.dataset.code
    const changed = !!code && code !== currentRegionCode
    if (code && changed) {
      setRegionCookie(code)
    } else {
      e.preventDefault()
    }
    onRegionSelect?.(e, changed)
  }, [currentRegionCode, onRegionSelect])

  return <>
    {sortedRegions.length > 0 && <RegionSection>
      <SectionTitle variant="medium" weight="bold" colour="normal-contrast">
        <FormattedMessage
          id="GNl43FRFT7zDOJ6wnMW9"
          defaultMessage="Popular regions and currencies"
          description="Section title above the suggested regions and currencies in the region selector"
        />
      </SectionTitle>
      <RegionGrid>
        {sortedRegions.map(region => <RegionItem
          size="medium"
          key={region.code}
          startIcon={<Flag countryCode={region.code} />}
          endIcon={currentRegionCode === region.code ? <LineCheckIcon /> : undefined}
          title={
            <FormattedMessage
              id="nIOR1XQB-FNL0jcqglur"
              defaultMessage="{regionName}, {currencyCode}"
              description="Region name with currency code in the region selector"
              values={{
                regionName: region.name,
                currencyCode: <Colour value="light-foreground-alt">{region.currencyCode.toUpperCase()}</Colour>,
              }}
            />
          }
          data-code={region.code}
          onClick={onRegionClick}
          selected={currentRegionCode === region.code}
          href={`/${region.code.toLowerCase()}${regionlessUrl}`}
        />)}
      </RegionGrid>
    </RegionSection>}
    {otherRegions.length > 0 && <RegionSection>
      <SectionTitle variant="medium" weight="bold" colour="normal-contrast">
        <FormattedMessage
          id="mK8XmN3ckUe9rK8v9rHh"
          defaultMessage="All regions and currencies"
          description="Section title above the full alphabetical region and currency list in the region selector"
        />
      </SectionTitle>
      <RegionGrid>
        {otherRegions.map(region => <RegionItem
          size="medium"
          key={region.code}
          startIcon={<Flag countryCode={region.code} />}
          endIcon={currentRegionCode === region.code ? <LineCheckIcon /> : undefined}
          title={
            <FormattedMessage
              id="nIOR1XQB-FNL0jcqglur"
              defaultMessage="{regionName}, {currencyCode}"
              description="Region name with currency code in the region selector"
              values={{
                regionName: region.name,
                currencyCode: <Colour value="light-foreground-alt">{region.currencyCode.toUpperCase()}</Colour>,
              }}
            />
          }
          data-code={region.code}
          selected={currentRegionCode === region.code}
          onClick={onRegionClick}
          href={`/${region.code.toLowerCase()}${regionlessUrl}`}
        />)}
      </RegionGrid>
    </RegionSection>}
  </>
}

export default React.memo(NavBarRegionItems)
